import type { WheelEvent } from 'react';

/**
 * `onWheel` for a scrollable list rendered in a popover over a modal dialog.
 *
 * The dialog locks scrolling to its own content, and a popover is portalled
 * outside that content, so the lock cancels every wheel event over the list:
 * it only moved by dragging its scrollbar. Making the popover `modal` would
 * fix the wheel but swallow the next click outside it — for a picker that
 * stays open while several things are ticked, that is a click lost on every
 * booking.
 *
 * So scroll the list ourselves, but only when the wheel really was blocked:
 * checked a frame later, after the lock's own listener has run. Outside a
 * dialog nothing blocks it, the browser scrolls natively, and this does
 * nothing — so the list never moves twice.
 */
export function scrollWhenWheelBlocked(event: WheelEvent<HTMLElement>): void {
  const list = event.currentTarget;
  const native = event.nativeEvent;
  // deltaMode: 0 = pixels, 1 = lines, 2 = pages.
  const lineHeight = 16;
  const deltaY =
    native.deltaMode === 1
      ? native.deltaY * lineHeight
      : native.deltaMode === 2
        ? native.deltaY * list.clientHeight
        : native.deltaY;
  requestAnimationFrame(() => {
    if (native.defaultPrevented) list.scrollTop += deltaY;
  });
}
